import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './GameResultModal.css';
import { formatNumber } from '../../utils/formatters';
import SoundManager from '../../services/SoundManager';

interface GameResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: {
    won: boolean;
    cost: number;
    newBalance: number;
    gameType: string;
    boost?: {
      multiplier: number;
      duration: number;
      expires_at: string;
    };
  } | null;
}

const GameResultModal: React.FC<GameResultModalProps> = ({ isOpen, onClose, result }) => {
  const { t } = useTranslation();
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen && result) {
      SoundManager.playModalOpen();
      setIsAnimating(true);

      // Если выиграли и получили буст, dispatch события для HomePage
      if (result.won && result.boost) {
        const boostEvent = new CustomEvent('boostActivated', {
          detail: {
            multiplier: result.boost.multiplier,
            duration: result.boost.duration,
            expiresAt: result.boost.expires_at
          }
        });
        window.dispatchEvent(boostEvent);
      }

      // НЕ закрываем автоматически - только по кнопке
    }
  }, [isOpen, result]);

  if (!isOpen || !result) return null;

  const getGameName = (gameType: string) => {
    const names: { [key: string]: string } = {
      'lunar_lottery': `🎰 ${t('games.lunarLottery.title')}`,
      'star_flip': `⭐ ${t('games.starFlip.title')}`,
      'cosmic_dice': `🎲 ${t('games.cosmicDice.title')}`,
      'meteor_race': `☄️ ${t('games.meteorRace.title')}`,
      'asteroid_miner': `⛏️ ${t('games.asteroidMiner.title')}`
    };
    return names[gameType] || gameType;
  };

  return (
    <div className={`game-result-modal ${isAnimating ? 'show' : ''}`}>
      <div className="game-result-overlay" onClick={onClose}></div>
      <div className={`game-result-content ${result.won ? 'win' : 'lose'}`}>
        
        {/* Анимация результата */}
        <div className="result-animation">
          {result.won ? (
            <>
              <div className="win-stars">
                <span className="star star-1">⭐</span>
                <span className="star star-2">🌟</span>
                <span className="star star-3">✨</span>
                <span className="star star-4">💫</span>
                <span className="star star-5">⭐</span>
              </div>
              <div className="win-icon">🏆</div>
            </>
          ) : (
            <div className="lose-icon">😔</div>
          )}
        </div>

        {/* Заголовок */}
        <h2 className={`result-title ${result.won ? 'win-title' : 'lose-title'}`}>
          {result.won ? t('modals.gameResultModal.victory') : t('modals.gameResultModal.defeat')}
        </h2>

        {/* Название игры */}
        <div className="game-name">{getGameName(result.gameType)}</div>

        {/* Детали результата */}
        <div className="result-details">
          {result.won && result.boost ? (
            <>
              <div className="boost-activation">
                <div className="boost-icon-large">⚡</div>
                <div className="boost-info">
                  <div className="boost-title">{t('modals.gameResultModal.boostActivated')}</div>
                  <div className="boost-multiplier">{t('modals.gameResultModal.profit')} x{result.boost.multiplier}</div>
                  <div className="boost-duration">{t('modals.gameResultModal.duration')} {result.boost.duration} {t('modals.gameResultModal.seconds')}</div>
                </div>
              </div>
              <div className="boost-hint">
                {t('modals.gameResultModal.incomeIncreased')} {result.boost.multiplier} {t('modals.gameResultModal.times')}!
              </div>
            </>
          ) : (
            <>
              <div className="result-row no-boost-message">
                <span className="label">{t('modals.gameResultModal.result')}</span>
                <span className="value">{t('modals.gameResultModal.noBoost')}</span>
              </div>
              <div className="motivational-sub">
                {t('modals.gameResultModal.tryAgainTomorrow')}
              </div>
            </>
          )}
        </div>

        {/* Новый баланс */}
        <div className="new-balance">
          <span className="balance-label">{t('modals.gameResultModal.newBalance')}</span>
          <span className="balance-value">{formatNumber(result.newBalance)} SPERM</span>
        </div>

        {/* Мотивационный текст */}
        <div className="motivational-text">
          {result.won
            ? t('modals.gameResultModal.greatGame')
            : t('modals.gameResultModal.dontGiveUp')}
        </div>

        {/* Кнопка закрытия */}
        <button className="close-button" onClick={onClose}>
          {result.won ? t('modals.gameResultModal.excellent') : t('modals.gameResultModal.tryAgain')}
        </button>
      </div>
    </div>
  );
};

export default GameResultModal;